import { useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteSegment } from 'api/segments';
import { FilterProviderContext } from 'components/filter/filters-next/FilterProvider';
import { SegmentContext } from 'components/filter/filters-next/SegmentContext';
import { defaultFilterGroups } from 'components/filter/filters-next/defaults';
import { ModelType } from 'components/filter/types-legacy';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentSegment } from 'customHooks/useCurrentSegment';
import { segmentQueryKeys } from 'queries/segment-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface DeleteSegmentDialogProps extends AlertDialog.AlertDialogRootProps {
    segmentId: number;
}

const DeleteSegmentDialog = (props: DeleteSegmentDialogProps) => {
    const { children, segmentId, ...rest } = props;
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();
    const { cacheKey, resource } = useContext(FilterProviderContext);
    const isViewingSegment = useCurrentSegment();
    const { setName, setIsEditing } = useContext(SegmentContext);
    const setFilters = useFiltersCache(state => state.updateFiltersNextByKey);
    const setSegmentId = useFiltersCache(state => state.setSegmentIdByKey);

    const { mutate: deleteSeg } = useMutation({
        mutationFn: () => deleteSegment({ accountId, segmentId }),
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Failed to update segment');
        },
        onSuccess: () => {
            if (isViewingSegment?.id === segmentId) {
                setName('');
                setIsEditing(false);
                setSegmentId(cacheKey);
                setFilters(cacheKey, defaultFilterGroups);
            }
            success('Deleted segment successfully');
            queryClient.invalidateQueries({
                queryKey: segmentQueryKeys.all(accountId, resource as ModelType),
            });
        },
    });

    return (
        <AlertDialog.Root {...rest} type={'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete segment?</AlertDialog.Title>
                        <AlertDialog.Description>
                            You can't undo this action afterwards.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => deleteSeg()}>Delete</AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteSegmentDialogTrigger = AlertDialog.Trigger;

const Root = DeleteSegmentDialog;
const Trigger = DeleteSegmentDialogTrigger;

export {
    DeleteSegmentDialog,
    DeleteSegmentDialogTrigger,
    //
    Root,
    Trigger,
};
